import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { JUPAS_STEP_ICON_COLOR } from '../../utils/jupasOrderDisplay';

const STEPS = ['scores', 'programmes', 'ranking'];

const DESKTOP_COLUMNS = {
  scores: 'minmax(0,1fr) 8.25rem 8.25rem',
  programmes: '8.25rem minmax(0,1fr) 8.25rem',
  ranking: '8.25rem 8.25rem minmax(0,1fr)',
};

const MOBILE_COLUMNS = {
  scores: 'minmax(0,1fr) 2.25rem 2.25rem',
  programmes: '2.25rem minmax(0,1fr) 2.25rem',
  ranking: '2.25rem 2.25rem minmax(0,1fr)',
};

/** FLIP timing: unhurried travel with a soft, decisive landing. */
const STAGE_EASE = 'cubic-bezier(0.25, 1, 0.5, 1)';
const STAGE_DURATION_MS = 540;

function useIsNarrow(breakpointPx = 768) {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(`(max-width: ${breakpointPx - 1}px)`).matches : false
  );

  useEffect(() => {
    const media = window.matchMedia(`(max-width: ${breakpointPx - 1}px)`);
    const onChange = () => setNarrow(media.matches);
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [breakpointPx]);

  return narrow;
}

function StepHeader({ stepNumber, title, titleId, compact = false, vertical = false, accessory = null }) {
  const { i18n } = useTranslation();
  const isChinese = /^(zh|tc|sc)(-|$)/i.test(i18n.resolvedLanguage || i18n.language || '');
  const stepIcon = (
    <span
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
      style={{ backgroundColor: JUPAS_STEP_ICON_COLOR }}
    >
      {stepNumber}
    </span>
  );

  if (vertical) {
    return (
      <div className="flex h-full flex-col items-center">
        <div className="flex h-[3.25rem] shrink-0 items-center justify-center">
          {stepIcon}
        </div>
        <h2
          id={titleId}
          className="-mt-1 max-h-44 overflow-hidden text-center text-base font-bold leading-none text-ink sm:text-lg"
          style={{
            writingMode: isChinese ? 'vertical-rl' : 'horizontal-tb',
            textOrientation: isChinese ? 'upright' : undefined,
            letterSpacing: isChinese ? '0.12em' : undefined,
          }}
        >
          {isChinese
            ? title
            : Array.from(title.toUpperCase()).map((character, index) => (
                <span key={`${character}-${index}`} className="block">
                  {character}
                </span>
              ))}
        </h2>
      </div>
    );
  }

  return (
    <div className={`flex h-[3.25rem] items-center gap-2 ${compact ? '' : 'px-4 sm:px-5'}`}>
      {stepIcon}
      <h2
        id={titleId}
        className={`truncate text-base font-bold text-ink sm:text-lg ${
          isChinese ? 'tracking-[0.12em] md:tracking-normal' : ''
        }`}
      >
        {title}
      </h2>
      {accessory ? <div className="ml-auto min-w-0 text-right">{accessory}</div> : null}
    </div>
  );
}

function WorkspacePanel({
  step,
  stepNumber,
  title,
  active,
  disabled,
  panelId,
  onActivate,
  sidebar,
  children,
  panelRef,
  narrowEdge,
  headerAccessory,
}) {
  const { t } = useTranslation();
  const titleId = `${panelId}-title`;

  const activate = () => {
    if (!disabled && !active) onActivate(step);
  };

  return (
    <section
      ref={panelRef}
      id={panelId}
      aria-labelledby={titleId}
      aria-expanded={active}
      className={[
        'jupas-calculator-workspace__panel relative flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border',
        active
          ? 'jupas-calculator-workspace__panel--active border-edge bg-surface'
          : `jupas-calculator-workspace__panel--collapsed border-edge bg-surface-muted ${
              disabled ? 'jupas-calculator-workspace__panel--disabled opacity-60' : ''
            }`,
      ].join(' ')}
    >
      {/* Collapsed hit-target stays mounted so width can clip/expand with the grid. */}
      <button
        type="button"
        tabIndex={active ? -1 : undefined}
        className={[
          'jupas-calculator-workspace__layer jupas-calculator-workspace__layer--rail absolute inset-0 z-[1] flex w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-not-allowed',
          narrowEdge ? 'flex-col items-center px-0.5' : 'flex-col gap-1 px-2.5 pb-2.5',
          active ? 'jupas-calculator-workspace__layer--exit pointer-events-none' : 'jupas-calculator-workspace__layer--enter',
        ].join(' ')}
        onClick={activate}
        disabled={disabled || active}
        aria-expanded={active}
        aria-controls={panelId}
        aria-hidden={active}
        aria-label={t('jupa.workspace.activateStep', { step: stepNumber, title })}
      >
        <StepHeader
          stepNumber={stepNumber}
          title={title}
          titleId={active ? undefined : titleId}
          compact
          vertical={narrowEdge}
        />
        {!narrowEdge ? (
          <div className="jupas-calculator-workspace__rail min-h-0 flex-1 overflow-y-auto">
            {sidebar}
          </div>
        ) : null}
      </button>

      {/* Active page stays mounted so it can collapse/expand with the column. */}
      <div
        className={[
          'jupas-calculator-workspace__layer jupas-calculator-workspace__layer--body z-[2] flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden',
          active
            ? 'jupas-calculator-workspace__layer--enter relative'
            : 'jupas-calculator-workspace__layer--exit pointer-events-none absolute inset-0',
        ].join(' ')}
        aria-hidden={!active}
        {...(active ? {} : { inert: '' })}
      >
        <div className="shrink-0" aria-hidden={!active}>
          <StepHeader stepNumber={stepNumber} title={title} titleId={active ? titleId : undefined} accessory={headerAccessory} />
        </div>
        <div className="-mt-1 flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-auto px-4 pb-4 pt-0 md:mt-0 md:px-5 md:pb-5 md:pt-1">
          {children}
        </div>
      </div>
    </section>
  );
}

/**
 * Three-panel calculator workspace: scores | programmes | ranking.
 * Single mount of each panel; column widths animate with activeStep.
 */
export default function JupasCalculatorWorkspace({
  activeStep,
  onStepChange,
  scoresComplete,
  programmesComplete,
  scoresSidebar,
  programmesSidebar,
  rankingSidebar,
  scoresContent,
  programmesContent,
  rankingContent,
  scoresHeaderAccessory,
  programmesHeaderAccessory,
  rankingHeaderAccessory,
  scoresRef,
  programmesRef,
  rankingRef,
}) {
  const { t } = useTranslation();
  const baseId = useId();
  const isNarrow = useIsNarrow(768);
  const cellRefs = useRef([]);
  const previousRectsRef = useRef(null);
  const previousStepRef = useRef(activeStep);
  const animationRunRef = useRef(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  );

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduceMotion(media.matches);
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const previousStepIndex = STEPS.indexOf(previousStepRef.current);
  const activeStepIndex = STEPS.indexOf(activeStep);
  const direction = activeStepIndex >= previousStepIndex ? 1 : -1;

  useLayoutEffect(() => {
    const cells = cellRefs.current.filter(Boolean);
    const nextRects = cells.map((cell) => cell.getBoundingClientRect());
    const previousRects = previousRectsRef.current;
    const stepChanged = previousStepRef.current !== activeStep;

    previousRectsRef.current = nextRects;
    previousStepRef.current = activeStep;

    if (reduceMotion || !stepChanged || !previousRects || previousRects.length !== nextRects.length) {
      setIsAnimating(false);
      return undefined;
    }

    const run = animationRunRef.current + 1;
    animationRunRef.current = run;
    setIsAnimating(true);

    const animations = cells.map((cell, index) => {
      const first = previousRects[index];
      const last = nextRects[index];
      const deltaX = first.left - last.left;
      const scaleX = last.width > 0 ? first.width / last.width : 1;

      cell.getAnimations().forEach((animation) => animation.cancel());
      return cell.animate(
        [
          { transform: `translate3d(${deltaX}px, 0, 0) scaleX(${scaleX})` },
          { transform: 'translate3d(0, 0, 0) scaleX(1)' },
        ],
        {
          duration: STAGE_DURATION_MS,
          easing: STAGE_EASE,
          fill: 'both',
        }
      );
    });

    Promise.allSettled(animations.map((animation) => animation.finished)).then(() => {
      if (animationRunRef.current === run) setIsAnimating(false);
    });

    return () => animations.forEach((animation) => animation.cancel());
  }, [activeStep, isNarrow, reduceMotion]);

  const titles = {
    scores: t('jupa.workspace.stepScores'),
    programmes: t('jupa.workspace.stepProgrammes'),
    ranking: t('jupa.workspace.stepRanking'),
  };

  const disabled = {
    scores: false,
    programmes: !scoresComplete,
    ranking: !programmesComplete,
  };

  const sidebars = {
    scores: scoresSidebar,
    programmes: programmesSidebar,
    ranking: rankingSidebar,
  };

  const contents = {
    scores: scoresContent,
    programmes: programmesContent,
    ranking: rankingContent,
  };

  const refs = {
    scores: scoresRef,
    programmes: programmesRef,
    ranking: rankingRef,
  };

  const columns = (isNarrow ? MOBILE_COLUMNS : DESKTOP_COLUMNS)[activeStep]
    || DESKTOP_COLUMNS.scores;

  return (
    <div
      className={`jupas-calculator-workspace grid min-h-[28rem] min-w-0 gap-2 md:min-h-[32rem] md:gap-3 ${
        isAnimating ? 'jupas-calculator-workspace--animating' : ''
      }`}
      style={{
        gridTemplateColumns: columns,
        '--jupas-body-exit-offset': `${direction * -0.55}rem`,
        '--jupas-rail-exit-offset': `${direction * 0.4}rem`,
      }}
    >
      {STEPS.map((step) => {
        const isActive = activeStep === step;
        return (
          <div
            key={step}
            ref={(node) => { cellRefs.current[STEPS.indexOf(step)] = node; }}
            className={`jupas-calculator-workspace__cell min-h-0 min-w-0 ${
              isActive ? 'jupas-calculator-workspace__cell--active' : ''
            }`}
          >
            <WorkspacePanel
              step={step}
              stepNumber={STEPS.indexOf(step) + 1}
              title={titles[step]}
              active={isActive}
              disabled={disabled[step]}
              panelId={`${baseId}-${step}`}
              onActivate={onStepChange}
              sidebar={sidebars[step]}
              panelRef={refs[step]}
              narrowEdge={isNarrow && !isActive}
              headerAccessory={
                step === 'scores'
                  ? scoresHeaderAccessory
                  : step === 'programmes'
                    ? programmesHeaderAccessory
                  : step === 'ranking'
                    ? rankingHeaderAccessory
                    : null
              }
            >
              {contents[step]}
            </WorkspacePanel>
          </div>
        );
      })}
    </div>
  );
}
